import { supabase } from './supabase';
import PromocodeDecoder from './promocodeDecoder.js';
import EnhancedPromocodeRedemption from './enhancedPromocodeRedemption.js';
import { PROMOCODE_CONFIG, PromocodeHelpers } from '../config/promocode.js';
import ExcelPromocodeUploader from './excelPromocodeUploader.js';
import PromocodeGenerator from './promocodeGenerator.js';

class PromocodeService {
  constructor() {
    this.supabase = supabase;
  }

  // Validate promocode without redeeming it
  async validatePromocode(code) {
    try {
      // First decode the promocode to get platform and plan details
      const decoded = PromocodeDecoder.decode(code);
      
      if (!decoded.isValid) {
        return { success: false, error: decoded.error || 'Invalid promocode format' };
      }

      // Check if promocode exists in database (still use existing table for backward compatibility)
      const { data, error } = await this.supabase
        .from(PROMOCODE_CONFIG.tableName)
        .select('code, is_used, start_date, end_date, credits_amount')
        .eq('code', code)
        .single();

      if (error) {
        console.error('Error validating promocode:', error);
        return { success: false, error: 'Invalid promocode' };
      }

      if (!data) {
        return { success: false, error: 'Invalid promocode' };
      }

      const currentDate = new Date().toISOString().split('T')[0];

      // Check if promocode is not yet active
      if (data.start_date > currentDate) {
        return { success: false, error: 'Promocode is not yet active' };
      }

      // Check if promocode is expired
      if (data.end_date < currentDate) {
        return { success: false, error: 'Promocode has expired' };
      }

      // Check if promocode is already used
      if (data.is_used) {
        return { success: false, error: 'Promocode has already been used' };
      }

      // Validate decoded promocode expiry
      if (!PromocodeDecoder.isPromocodeValid(decoded)) {
        return { success: false, error: 'Promocode has expired' };
      }

      return { 
        success: true, 
        valid: true,
        decoded: decoded,
        startDate: data.start_date,
        endDate: data.end_date,
        creditsAmount: decoded.credits, // Use decoded credits instead of database credits
        platform: decoded.platform,
        planType: decoded.planType
      };
    } catch (error) {
      console.error('Error validating promocode:', error);
      return { success: false, error: 'Failed to validate promocode' };
    }
  }

  // Redeem promocode and add credits
  async redeemPromocode(code) {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'User not authenticated' };
      }

      // First validate the promocode
      const validation = await this.validatePromocode(code);
      if (!validation.success) {
        return validation;
      }

      // Get decoded promocode details
      const decoded = validation.decoded;
      const creditsAmount = decoded.credits;
      
      // Get credit system details for this promocode
      const creditSystemDetails = PromocodeDecoder.getCreditSystemDetails(decoded);
      
      // Get ALL existing plans for the user (not just exact matches)
      const { data: existingPlans, error: existingPlansError } = await this.supabase
        .from('email_validation_credits')
        .select('*')
        .eq('user_id', session.user.id)
        .order('activation_date', { ascending: false });

      if (existingPlansError) {
        console.error('Error checking existing plans:', existingPlansError);
        return { success: false, error: 'Failed to check existing plans' };
      }

      // Use enhanced logic to find the best plan to merge with
      const mergeResult = EnhancedPromocodeRedemption.findBestPlanToMerge(existingPlans, creditSystemDetails);

      if (mergeResult) {
        // Merge with existing plan
        const existingPlan = mergeResult.plan;
        const strategy = mergeResult.strategy;
        
        console.log(`Merging with existing plan using strategy: ${strategy}`);
        
        // Calculate new values based on strategy
        let newCreditsTotal, newCreditsRemaining, newPlanLimit, newResetDate, newSubscriptionPlan, newBillingCycle;
        
        // Debug logging
        console.log('🔍 DEBUG: Calculating reset date for strategy:', strategy);
        console.log('  - Existing plan billing cycle:', existingPlan.billing_cycle);
        console.log('  - Existing plan reset date:', existingPlan.reset_date);
        console.log('  - Credits amount:', creditsAmount);
        console.log('  - Credit system details billing cycle:', creditSystemDetails.billingCycle);
        
        switch (strategy) {
          case 'MERGE_EXACT':
          case 'MERGE_SAME_BILLING':
            // Add credits but keep the same reset date (no extension)
            newCreditsTotal = existingPlan.credits_total + creditsAmount;
            newCreditsRemaining = existingPlan.credits_remaining + creditsAmount;
            newPlanLimit = existingPlan.plan_limit + creditsAmount;
            newResetDate = existingPlan.reset_date; // Keep same reset date
            newSubscriptionPlan = existingPlan.subscription_plan;
            newBillingCycle = existingPlan.billing_cycle;
            break;
            
          case 'MERGE_SAME_PLATFORM':
            // Same platform, different billing cycle - extend reset date
            newCreditsTotal = existingPlan.credits_total + creditsAmount;
            newCreditsRemaining = existingPlan.credits_remaining + creditsAmount;
            newPlanLimit = existingPlan.plan_limit + creditsAmount;
            // For same platform with different billing cycle, extend reset date
            newResetDate = EnhancedPromocodeRedemption.calculateNewResetDate(existingPlan.billing_cycle, existingPlan.reset_date, creditsAmount);
            newSubscriptionPlan = existingPlan.subscription_plan;
            newBillingCycle = existingPlan.billing_cycle;
            break;
            
          case 'ACCUMULATE_TO_LIFETIME':
            // Add credits to lifetime plan (keep same reset date)
            newCreditsTotal = existingPlan.credits_total + creditsAmount;
            newCreditsRemaining = existingPlan.credits_remaining + creditsAmount;
            newPlanLimit = existingPlan.plan_limit + creditsAmount;
            newResetDate = existingPlan.reset_date; // Keep same reset date
            newSubscriptionPlan = existingPlan.subscription_plan;
            newBillingCycle = 'yearly';
            break;
            
          case 'UPGRADE_TO_LIFETIME':
            // Upgrade to lifetime plan - always extend by 10 years
            newCreditsTotal = existingPlan.credits_total + creditsAmount;
            newCreditsRemaining = existingPlan.credits_remaining + creditsAmount;
            newPlanLimit = existingPlan.plan_limit + creditsAmount;
            // For lifetime upgrade, always extend by 10 years from existing reset date
            const lifetimeResetDate = new Date(existingPlan.reset_date);
            lifetimeResetDate.setFullYear(lifetimeResetDate.getFullYear() + 10);
            newResetDate = lifetimeResetDate.toISOString().split('T')[0];
            newSubscriptionPlan = creditSystemDetails.subscriptionPlan;
            newBillingCycle = 'yearly';
            break;
            
          case 'UPGRADE_BILLING_CYCLE':
            // Upgrade billing cycle (monthly -> yearly)
            newCreditsTotal = existingPlan.credits_total + creditsAmount;
            newCreditsRemaining = existingPlan.credits_remaining + creditsAmount;
            newPlanLimit = existingPlan.plan_limit + creditsAmount;
            // For billing cycle upgrade, always add 1 year from existing reset date
            const currentResetDate = new Date(existingPlan.reset_date);
            currentResetDate.setFullYear(currentResetDate.getFullYear() + 1);
            newResetDate = currentResetDate.toISOString().split('T')[0];
            newSubscriptionPlan = existingPlan.subscription_plan; // Keep same platform
            newBillingCycle = creditSystemDetails.billingCycle; // Use new billing cycle
            break;
            
          default:
            // Fallback to exact match behavior (keep same reset date)
            newCreditsTotal = existingPlan.credits_total + creditsAmount;
            newCreditsRemaining = existingPlan.credits_remaining + creditsAmount;
            newPlanLimit = existingPlan.plan_limit + creditsAmount;
            newResetDate = existingPlan.reset_date; // Keep same reset date
            newSubscriptionPlan = existingPlan.subscription_plan;
            newBillingCycle = existingPlan.billing_cycle;
        }
        
        // Debug logging for calculated values
        console.log('🔍 DEBUG: Calculated values:');
        console.log('  - New reset date:', newResetDate);
        console.log('  - New billing cycle:', newBillingCycle);
        console.log('  - New subscription plan:', newSubscriptionPlan);

        const { data: updateData, error: updateError } = await this.supabase
          .from('email_validation_credits')
          .update({
            credits_total: newCreditsTotal,
            credits_remaining: newCreditsRemaining,
            plan_limit: newPlanLimit,
            reset_date: newResetDate,
            subscription_plan: newSubscriptionPlan,
            billing_cycle: newBillingCycle,
            last_updated: new Date().toISOString()
          })
          .eq('id', existingPlan.id)
          .select();

        if (updateError) {
          console.error('Error updating credits:', updateError);
          return { success: false, error: 'Failed to update credits' };
        }

        // Get user-friendly message
        const message = EnhancedPromocodeRedemption.getRedemptionMessage(
          { strategy }, 
          existingPlan, 
          creditSystemDetails
        );

        // Mark promocode as used
        const { error: promocodeUpdateError } = await this.supabase
          .from(PROMOCODE_CONFIG.tableName)
          .update({
            is_used: true,
            used_by: session.user.id,
            used_at: new Date().toISOString()
          })
          .eq('code', code);

        if (promocodeUpdateError) {
          console.error('Error marking promocode as used:', promocodeUpdateError);
          // Note: Credits were already added, so we don't return error here
          // Just log the issue for monitoring
        }

        return { 
          success: true, 
          message: message,
          strategy: strategy,
          creditsAdded: creditsAmount,
          totalCredits: newCreditsTotal,
          platform: creditSystemDetails.subscriptionPlan,
          planType: creditSystemDetails.planType
        };
      } else {
        // Create new plan
        console.log('Creating new plan - no suitable existing plan found');
        
        // Debug logging
        console.log('🔍 DEBUG: Creating new plan');
        console.log('  - Credit system details billing cycle:', creditSystemDetails.billingCycle);
        console.log('  - Credits amount:', creditsAmount);
        console.log('  - Plan type:', creditSystemDetails.planType);
        console.log('  - Current date:', new Date().toISOString().split('T')[0]);
        
        // For first-time redemptions, use plan type to determine reset date
        let resetDate;
        const currentDate = new Date().toISOString().split('T')[0];
        
        if (creditSystemDetails.planType === 'lifetime') {
          // Lifetime plan: +10 years
          const lifetimeDate = new Date(currentDate);
          lifetimeDate.setFullYear(lifetimeDate.getFullYear() + 10);
          resetDate = lifetimeDate.toISOString().split('T')[0];
        } else if (creditSystemDetails.billingCycle === 'monthly') {
          // Monthly plan: +1 month
          const monthlyDate = new Date(currentDate);
          monthlyDate.setMonth(monthlyDate.getMonth() + 1);
          resetDate = monthlyDate.toISOString().split('T')[0];
        } else {
          // Yearly plan: +1 year (regardless of credits amount)
          const yearlyDate = new Date(currentDate);
          yearlyDate.setFullYear(yearlyDate.getFullYear() + 1);
          resetDate = yearlyDate.toISOString().split('T')[0];
        }
        
        const activationDate = new Date().toISOString().split('T')[0];
        
        // Debug logging for calculated values
        console.log('🔍 DEBUG: Calculated values for new plan:');
        console.log('  - Reset date:', resetDate);
        console.log('  - Activation date:', activationDate);

        const { data: creditData, error: creditError } = await this.supabase
          .from('email_validation_credits')
          .insert({
            user_id: session.user.id,
            subscription_plan: creditSystemDetails.subscriptionPlan,
            credits_total: creditsAmount,
            credits_remaining: creditsAmount,
            credits_used: 0,
            plan_limit: creditsAmount,
            billing_cycle: creditSystemDetails.billingCycle,
            reset_date: resetDate,
            activation_date: activationDate,
            created_at: new Date().toISOString()
          })
          .select();

        if (creditError) {
          console.error('Error creating credits:', creditError);
          return { success: false, error: 'Failed to add credits' };
        }

        const message = EnhancedPromocodeRedemption.getRedemptionMessage(
          { strategy: 'CREATE_SEPARATE' }, 
          null, 
          creditSystemDetails
        );

        // Mark promocode as used
        const { error: promocodeUpdateError2 } = await this.supabase
          .from(PROMOCODE_CONFIG.tableName)
          .update({
            is_used: true,
            used_by: session.user.id,
            used_at: new Date().toISOString()
          })
          .eq('code', code);

        if (promocodeUpdateError2) {
          console.error('Error marking promocode as used:', promocodeUpdateError2);
          // Note: Credits were already added, so we don't return error here
          // Just log the issue for monitoring
        }

        return { 
          success: true, 
          message: message,
          strategy: 'CREATE_SEPARATE',
          creditsAdded: creditsAmount,
          totalCredits: creditsAmount,
          platform: creditSystemDetails.subscriptionPlan,
          planType: creditSystemDetails.planType
        };
      }
    } catch (error) {
      console.error('Error redeeming promocode:', error);
      return { success: false, error: 'Failed to redeem promocode' };
    }
  }


  // Get user's used promocodes (for display purposes)
  async getUserPromocodes() {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'User not authenticated' };
      }

      const { data, error } = await this.supabase
        .from(PROMOCODE_CONFIG.tableName)
        .select('code, used_at, end_date')
        .eq('used_by', session.user.id)
        .eq('is_used', true)
        .order('used_at', { ascending: false });

      if (error) {
        console.error('Error getting user promocodes:', error);
        return { success: false, error: 'Failed to get promocodes' };
      }

      return { success: true, promocodes: data || [] };
    } catch (error) {
      console.error('Error getting user promocodes:', error);
      return { success: false, error: 'Failed to get promocodes' };
    }
  }

  // Upload promocodes from Excel (admin only)
  async uploadPromocodesFromExcel(excelData) {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'User not authenticated' };
      }

      // Check if user is admin
      const { data: userProfile, error: profileError } = await this.supabase
        .from('user_profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (profileError || !userProfile || userProfile.role !== 'admin') {
        return { success: false, error: 'Admin access required' };
      }

      // Upload promocodes from Excel data
      const result = await ExcelPromocodeUploader.uploadPromocodesToDatabase(excelData);

      return result;
    } catch (error) {
      console.error('Error uploading promocodes:', error);
      return { success: false, error: 'Failed to upload promocodes' };
    }
  }

  // New methods for unified promocode system

  // Generate promocodes using the generator
  async generatePromocodes(config) {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'User not authenticated' };
      }

      // Check if user is admin
      const { data: userProfile, error: profileError } = await this.supabase
        .from('user_profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (profileError || !userProfile || userProfile.role !== 'admin') {
        return { success: false, error: 'Admin access required' };
      }

      // Validate that platform exists
      const platform = PromocodeHelpers.getPlatformByName(config.platform);
      if (!platform) {
        return { success: false, error: `Platform '${config.platform}' not found. Please add the platform first.` };
      }

      // Generate promocodes
      const codes = PromocodeGenerator.generateBulk(config, config.quantity || 1);
      
      return { success: true, codes };
    } catch (error) {
      console.error('Error generating promocodes:', error);
      return { success: false, error: 'Failed to generate promocodes' };
    }
  }

  // Bulk create promocodes in database
  async bulkCreatePromocodes(codes, config) {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'User not authenticated' };
      }

      // Check if user is admin
      const { data: userProfile, error: profileError } = await this.supabase
        .from('user_profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (profileError || !userProfile || userProfile.role !== 'admin') {
        return { success: false, error: 'Admin access required' };
      }

      // Prepare promocodes for database insertion
      const promocodes = codes.map(code => ({
        code: code,
        start_date: config.startDate || new Date().toISOString().split('T')[0],
        end_date: config.endDate || new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
        credits_amount: config.credits || PROMOCODE_CONFIG.defaults.credits,
        created_at: new Date().toISOString()
      }));

      const { data, error } = await this.supabase
        .from(PROMOCODE_CONFIG.tableName)
        .insert(promocodes)
        .select();

      if (error) {
        console.error('Error bulk creating promocodes:', error);
        return { success: false, error: 'Failed to create promocodes' };
      }

      return { success: true, data, count: promocodes.length };
    } catch (error) {
      console.error('Error bulk creating promocodes:', error);
      return { success: false, error: 'Failed to create promocodes' };
    }
  }

  // Get promocode analytics
  async getPromocodeAnalytics(filters = {}) {
    try {
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session) {
        return { success: false, error: 'User not authenticated' };
      }

      // Check if user is admin
      const { data: userProfile, error: profileError } = await this.supabase
        .from('user_profiles')
        .select('role')
        .eq('id', session.user.id)
        .single();

      if (profileError || !userProfile || userProfile.role !== 'admin') {
        return { success: false, error: 'Admin access required' };
      }

      const { data, error } = await this.supabase
        .from(PROMOCODE_CONFIG.tableName)
        .select('*')
        .order('created_at', { ascending: false });

      if (error) {
        console.error('Error getting promocode analytics:', error);
        return { success: false, error: 'Failed to get analytics' };
      }

      const analytics = {
        total: data.length,
        used: data.filter(p => p.is_used).length,
        active: data.filter(p => !p.is_used && new Date(p.end_date) >= new Date()).length,
        expired: data.filter(p => new Date(p.end_date) < new Date()).length,
        platformBreakdown: this.calculatePlatformBreakdown(data),
        creditDistribution: this.calculateCreditDistribution(data)
      };

      return { success: true, analytics };
    } catch (error) {
      console.error('Error getting promocode analytics:', error);
      return { success: false, error: 'Failed to get analytics' };
    }
  }

  // Helper method to calculate platform breakdown
  calculatePlatformBreakdown(promocodes) {
    const breakdown = {};
    promocodes.forEach(promo => {
      const decoded = PromocodeDecoder.decode(promo.code);
      if (decoded.isValid) {
        const platform = decoded.platform;
        breakdown[platform] = (breakdown[platform] || 0) + 1;
      }
    });
    return breakdown;
  }

  // Helper method to calculate credit distribution
  calculateCreditDistribution(promocodes) {
    const distribution = {};
    promocodes.forEach(promo => {
      const credits = promo.credits_amount;
      const range = this.getCreditRange(credits);
      distribution[range] = (distribution[range] || 0) + 1;
    });
    return distribution;
  }

  // Helper method to get credit range
  getCreditRange(credits) {
    if (credits < 1000) return 'Under 1K';
    if (credits < 10000) return '1K-10K';
    if (credits < 100000) return '10K-100K';
    if (credits < 1000000) return '100K-1M';
    return 'Over 1M';
  }

}

export default new PromocodeService();
